// src/components/editor/LayerPanel.jsx
import React, { useState } from 'react';
import { 
  Layers, Type, Image as ImageIcon, Video, Tag, ChevronUp, 
  ChevronDown, Trash2, Eye, EyeOff, Copy 
} from 'lucide-react';

const LayerPanel = ({ 
  elements, 
  selectedElement, 
  onSelectElement, 
  onUpdateElement, 
  onDeleteElement, 
  onDuplicateElement,
  onMoveLayer 
}) => {
  const [groupBy, setGroupBy] = useState('none');
  const [showHidden, setShowHidden] = useState(true);

  const getElementIcon = (type) => {
    switch (type) {
      case 'text': return <Type className="h-4 w-4" />;
      case 'image': return <ImageIcon className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      default: return <div className="w-4 h-4 bg-gray-400 rounded" />;
    }
  };

  const getElementName = (element) => {
    switch (element.type) {
      case 'text':
        return element.content?.length > 18 
          ? element.content.substring(0, 18) + '...' 
          : element.content || 'Texte vide';
      case 'image':
        return element.alt || 'Image';
      case 'video':
        return 'Vidéo';
      default:
        return 'Élément';
    }
  };

  const toggleVisibility = (elementId, e) => {
    e.stopPropagation();
    const element = elements.find(el => el.id === elementId);
    const newVisibility = element.visible === false ? true : false;
    onUpdateElement(elementId, { visible: newVisibility });
  };

  const groupElements = () => {
    let sortedElements = [...elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
    
    if (!showHidden) {
      sortedElements = sortedElements.filter(el => el.visible !== false);
    }

    switch (groupBy) {
      case 'type':
        const byType = sortedElements.reduce((acc, el) => {
          const type = el.type;
          if (!acc[type]) acc[type] = [];
          acc[type].push(el);
          return acc;
        }, {});
        return Object.entries(byType);

      case 'dynamic':
        const dynamicElements = sortedElements.filter(el => el.isDynamic);
        const staticElements = sortedElements.filter(el => !el.isDynamic);
        return [
          ['Dynamiques', dynamicElements],
          ['Statiques', staticElements]
        ].filter(([_, items]) => items.length > 0);

      default:
        return [['all', sortedElements]];
    }
  };

  const ElementRow = ({ element }) => {
    const isVisible = element.visible !== false;
    
    return (
      <div
        onClick={() => onSelectElement(element)}
        className={`flex items-center space-x-3 p-3 rounded cursor-pointer transition-colors group ${
          selectedElement?.id === element.id
            ? 'bg-blue-100 border border-blue-300'
            : 'hover:bg-gray-100'
        } ${!isVisible ? 'opacity-50' : ''}`}
      >
        {/* Icône et visibilité */}
        <div className="flex items-center space-x-2 flex-shrink-0">
          <button
            onClick={(e) => toggleVisibility(element.id, e)}
            className={`p-1 hover:bg-gray-200 rounded transition-all ${
              !isVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
            title={isVisible ? "Masquer" : "Afficher"}
          >
            {isVisible ? 
              <Eye className="h-4 w-4 text-gray-600" /> : 
              <EyeOff className="h-4 w-4 text-red-500" />
            }
          </button>
          
          <div className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded flex-shrink-0">
            {getElementIcon(element.type)}
          </div>
        </div>

        {/* Contenu principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm font-medium truncate">
              {getElementName(element)}
            </span>
            {element.isDynamic && (
              <Tag className="h-4 w-4 text-orange-600 flex-shrink-0" title="Variable" />
            )}
            {element.enablePriceFormat && (
              <span className="text-sm" title="Format prix">💰</span>
            )}
          </div>
          
          {/* Informations organisées */}
          <div className="text-xs text-gray-500 space-y-0.5">
            <div>Z: {element.zIndex || 1}</div>
            {element.isDynamic && element.dynamicTag && (
              <div className="text-orange-600 font-medium">
                {element.dynamicTag}
              </div>
            )}
          </div>
        </div>

        {/* Actions - visibles au survol */}
        <div className="flex items-center space-x-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveLayer(element.id, 'up');
            }}
            className="p-1.5 hover:bg-gray-200 rounded"
            title="Monter"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveLayer(element.id, 'down');
            }}
            className="p-1.5 hover:bg-gray-200 rounded"
            title="Descendre"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicateElement(element);
            }}
            className="p-1.5 hover:bg-blue-200 rounded text-blue-600"
            title="Dupliquer"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteElement(element.id);
            }}
            className="p-1.5 hover:bg-red-200 rounded text-red-600"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  const groupedElements = groupElements();

  return (
    <div className="full-height flex flex-col bg-white border-r">
      {/* En-tête */}
      <div className="p-4 border-b flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Couches</h3>
          <div className="text-xs text-gray-500">
            {elements.length} éléments
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3">
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm"
          >
            <option value="none">Ordre des couches</option>
            <option value="type">Grouper par type</option>
            <option value="dynamic">Grouper par contenu</option>
          </select>

          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="rounded"
            />
            <span>Afficher les éléments masqués</span>
          </label>
        </div>
      </div>

      {/* Liste des éléments */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-4">
          {groupedElements.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <Layers className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Aucun élément</p>
              <p className="text-xs mt-1">Ajoutez du contenu pour commencer</p>
            </div>
          ) : (
            <div className="space-y-2">
              {groupedElements.map(([groupName, groupElements]) => (
                <div key={groupName}>
                  {/* En-tête de groupe */}
                  {groupBy !== 'none' && (
                    <div className="flex items-center space-x-2 mb-3 pb-2 border-b">
                      <span className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                        {groupName} ({groupElements.length})
                      </span>
                    </div>
                  )}

                  {/* Éléments du groupe */}
                  <div className="space-y-1">
                    {groupElements.map(element => (
                      <ElementRow 
                        key={element.id} 
                        element={element}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Statistiques en bas */}
      <div className="p-4 border-t flex-shrink-0">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{elements.filter(el => el.visible !== false).length} visibles</span>
          <span>{elements.filter(el => el.isDynamic).length} dynamiques</span>
        </div>
      </div>
    </div>
  );
};

export default LayerPanel;